import React, {useEffect, useState} from 'react';

import {onAuthStateChanged} from 'firebase/auth';
import {getDoc, doc} from 'firebase/firestore';
import {db, auth} from '../auth/firebase';

const UserStatusBadge = () => {
    const [user, setUser] = useState(null);
    const [accountProfile, setAccountProfile] = useState(null);

    const handleLogout = async () => {
        try {
            sessionStorage.setItem('justLoggedOut', 'true');

            await auth.signOut();

            console.log('[EDITOR] 🔓 Signed out');
        } catch (err) {
            console.error(
                '[EDITOR] ❌ Sign-out error:',
                err
            );
        }
    };

    useEffect(() => {
        const unsubscribe =
            onAuthStateChanged(
                auth,
                async firebaseUser => {
                    if (!firebaseUser) {
                        setUser(null);
                        setAccountProfile(null);
                        return;
                    }

                    setUser(firebaseUser);

                    try {
                        /*
                         * Check company account first.
                         */
                        const companyRef =
                            doc(
                                db,
                                'companies',
                                firebaseUser.uid
                            );

                        const companySnap =
                            await getDoc(companyRef);

                        if (companySnap.exists()) {
                            const data =
                                companySnap.data();

                            setAccountProfile({
                                ...data,
                                accountType: 'company'
                            });

                            console.log(
                                '[FIRESTORE] ✅ Company profile:',
                                data
                            );

                            return;
                        }

                        /*
                         * If no company account exists,
                         * check regular profile users.
                         */
                        const profileRef =
                            doc(
                                db,
                                'profiles',
                                firebaseUser.uid
                            );

                        const profileSnap =
                            await getDoc(profileRef);

                        if (profileSnap.exists()) {
                            const data =
                                profileSnap.data();

                            setAccountProfile({
                                ...data,
                                accountType: 'profile'
                            });

                            console.log(
                                '[FIRESTORE] ✅ User profile:',
                                data
                            );

                            return;
                        }

                        console.warn(
                            '[FIRESTORE] ❌ No company or profile found'
                        );

                        /*
                         * Still allow the authenticated Firebase
                         * user to display using email as fallback.
                         */
                        setAccountProfile({
                            name:
                                firebaseUser.displayName ||
                                firebaseUser.email ||
                                'User',
                            accountType: 'firebase'
                        });
                    } catch (error) {
                        console.error(
                            '[FIRESTORE] ❌ Error fetching account profile:',
                            error
                        );

                        setAccountProfile({
                            name:
                                firebaseUser.displayName ||
                                firebaseUser.email ||
                                'User',
                            accountType: 'firebase'
                        });
                    }
                }
            );

        return () => unsubscribe();
    }, []);

    if (!user) {
        return (
            <div style={styles.badgeError}>
                <div style={styles.redDot} />

                <span style={styles.errorText}>
                    Not logged in
                </span>
            </div>
        );
    }

    if (user && accountProfile) {
        return (
            <div style={styles.badgeSuccess}>
                <div style={styles.greenDot} />

                {accountProfile.avatarUrl && (
                    <img
                        src={accountProfile.avatarUrl}
                        alt="avatar"
                        style={styles.avatar}
                    />
                )}

                <span style={styles.successText}>
                    {accountProfile.name ||
                        user.displayName ||
                        user.email}
                </span>

                <button
                    onClick={handleLogout}
                    style={styles.logoutButton}
                >
                    Log out
                </button>
            </div>
        );
    }

    return null;
};

const styles = {
    badgeError: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#fef2f2',
        padding: '6px 12px',
        borderRadius: '20px',
        boxShadow:
            '0 0 4px rgba(255, 0, 0, 0.4)',
        gap: '8px',
        border: '1px solid #fecaca'
    },

    redDot: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        backgroundColor: '#ef4444',
        boxShadow: '0 0 6px #ef4444'
    },

    errorText: {
        fontWeight: 'bold',
        fontSize: '0.9rem',
        color: '#b91c1c'
    },

    badgeSuccess: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#f0fdf4',
        padding: '6px 12px',
        borderRadius: '20px',
        boxShadow:
            '0 0 4px rgba(0, 255, 0, 0.5)',
        gap: '8px',
        border: '1px solid #bbf7d0'
    },

    greenDot: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        backgroundColor: '#22c55e',
        boxShadow: '0 0 6px #22c55e'
    },

    avatar: {
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        objectFit: 'cover'
    },

    successText: {
        fontWeight: 'bold',
        fontSize: '0.9rem',
        color: '#15803d'
    },

    logoutButton: {
        marginLeft: '10px',
        padding: '4px 8px',
        fontSize: '0.8rem',
        backgroundColor: '#f87171',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer'
    }
};

export default UserStatusBadge;